import PropTypes from 'prop-types';
import React, {useRef, useState} from 'react';
import numeral from 'numeral';

import Dropzone from 'react-dropzone';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
} from '@mui/material';

import {DialogTitle} from '@apps-common/ui-components';
import {ingestFiles, queryKeys, uploadToGCS} from '@api/asyncIngest';
import {useQueryClient} from '@tanstack/react-query';

// filetypes supported by the async ingest endpoint
const SUPPORTED_FILE_TYPES = {
  'application/json': ['.geojson', '.json'],
  'application/octet-stream': ['.shp', '.shx', '.gpx'],
  'application/x-dbf': ['.dbf'],
  'application/text': ['.prj', '.wkt'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
};

// steps in the async upload process
const STEP = {
  UPLOAD: 'upload',
  PROGRESS: 'progress',
};

const AsyncUpload = ({open = true, onClose = () => {}, inFiles = []}) => {
  const queryClient = useQueryClient();

  const [files, setFiles] = useState(inFiles);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(STEP.UPLOAD);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  // cancellation for the upload request
  const cancelToken = useRef(null);
  const requestCanceled = useRef(false);

  const resetState = () => {
    setFiles([]);
    setProcessing(false);
    setUploadError(null);
    setUploadProgress(0);

    cancelToken.current = null;
    requestCanceled.current = false;

    setStep(STEP.UPLOAD);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUploadSuccess = () => {
    setProcessing(false);
    queryClient.invalidateQueries({queryKey: queryKeys.lists()});
  };

  const handleUploadError = error => {
    let msg = null;

    // check for file errors
    const fileErrors = error?.body?.upload?.files;
    if (fileErrors && fileErrors.length) {
      // if there are specific file errors, display the first one.
      msg = `Error uploading file: ${fileErrors[0]}`;
    } else if (error?.body?.error) {
      // if no file errors, check for an "error" property in the response.
      msg = error.body.error;
    } else if (requestCanceled.current) {
      msg = 'Upload canceled.';
    } else {
      // if there are no specific errors and the request wasn't canceled,
      // show a generic error message
      msg = 'There was an error uploading the file.';
    }

    setProcessing(null);
    setUploadError(msg);
  };

  const onUploadProgress = progressEvent => {
    setUploadProgress(
      Math.round((progressEvent.loaded * 100) / progressEvent.total)
    );
  };

  const handleUpload = () => {
    setProcessing(true);
    setStep(STEP.PROGRESS);

    // create cancellation token for this request
    cancelToken.current = new AbortController();

    ingestFiles(files)
      .then(response =>
        uploadToGCS(response.data.urls, files, {
          onUploadProgress,
          signal: cancelToken.current.signal,
        })
      )
      .then(handleUploadSuccess)
      .catch(handleUploadError);
  };

  const handleCancelUpload = () => {
    if (cancelToken.current) {
      cancelToken.current.abort();
    }
    requestCanceled.current = true;
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle onClose={handleClose} title="Upload AOI" />
      <DialogContent>
        {step === STEP.UPLOAD && (
          <>
            <Dropzone
              onDrop={acceptedFiles => {
                setFiles(acceptedFiles);
              }}
              accept={SUPPORTED_FILE_TYPES}
            >
              {({getRootProps, getInputProps}) => (
                <Box
                  sx={{
                    margin: 2,
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    borderRadius: 1,
                    borderColor: 'primary.main',
                    backgroundColor: 'paper',
                    outline: 'none',
                    cursor: 'pointer',
                    transition:
                      'color .24s ease-in-out, border .24s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: files.length > 0 ? 'flex-start' : 'center',
                    flex: '1 1 auto',
                    position: 'relative',
                  }}
                >
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <p>Drag and drop files here, or click to select</p>
                  </div>
                </Box>
              )}
            </Dropzone>
            {!!files.length && (
              <Box>
                {files.map(file => (
                  <Box key={file.name}>
                    {file.name} ({numeral(file.size).format('0b')})
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
        {step === STEP.PROGRESS && (
          <>
            <Typography>Uploading:</Typography>
            <Box>
              {files.map(file => (
                <Box key={file.name}>
                  {file.name} ({numeral(file.size).format('0b')})
                </Box>
              ))}
            </Box>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              color={uploadError ? 'error' : 'primary'}
            />
            {!processing && !uploadError && (
              <Typography>
                File{files.length > 1 ? 's' : ''} uploaded. Check the status of
                your uploads in the bottom left navigation using the{' '}
                <LoopIcon /> icon.
              </Typography>
            )}
            {!processing && uploadError && (
              <Typography>{uploadError}</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === STEP.UPLOAD && (
          <Button
            color="primary"
            variant="contained"
            disabled={!files.length}
            onClick={handleUpload}
          >
            Continue
          </Button>
        )}

        {step === STEP.PROGRESS && (
          <>
            {processing && cancelToken && (
              <Button
                onClick={() => handleCancelUpload()}
                color="primary"
                variant="contained"
              >
                Cancel
              </Button>
            )}
            {!processing && (
              <Button
                onClick={resetState}
                color="primary"
                disabled={processing}
              >
                Upload another file
              </Button>
            )}
            <Button
              onClick={handleClose}
              color="primary"
              variant="contained"
              disabled={processing}
            >
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

AsyncUpload.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  inFiles: PropTypes.array,
};

export default AsyncUpload;
